import Log from '../../src-ts/common/Log';
import { CookieHelper } from './CookieHelper';

export class ItemManagementData {
	public readonly items: ItemWithPositionIndex[];
	public readonly placementPositionCount: number;

	private _selectedItemVoteIndex: number;
	public get selectedItemVoteIndex(): number {
		return this._selectedItemVoteIndex;
	}
	public get selectedItem(): ItemWithPositionIndex {
		return this.items[this.selectedItemVoteIndex];
	}

	public constructor(
		placementPositionCount: number,
		itemNames: string[],
		initialSelectedIndex: number,
		sessionIndex: number,
		cookieHelper: CookieHelper
	) {
		this.placementPositionCount = placementPositionCount;
		this._selectedItemVoteIndex = initialSelectedIndex;
		this.cookieHelper = cookieHelper;

		this.items = [];
		itemNames.forEach((itemName) => {
			this.items.push(new ItemWithPositionIndex(itemName));
		});

		this.tryRestoreSelectionsFromCookies(sessionIndex);
	}

	public incrementSelectedItemVoteIndex = (): void => {
		if (this.selectedItemVoteIndex >= this.items.length - 1) {
			return;
		}
		this._selectedItemVoteIndex++;
	};
	public decrementSelectedItemVoteIndex = (): void => {
		if (this.selectedItemVoteIndex <= 0) {
			return;
		}
		this._selectedItemVoteIndex--;
	};
	public resetSelectedItemVoteIndex = (): void => {
		this._selectedItemVoteIndex = 0;
	};

	public selectPositionForCurrentItem = (
		positionIndex: number,
		sessionIndex: number
	): [toggled: boolean, selected: boolean] => {
		Log.log(
			`Selecting position index ${positionIndex} for selected item ${this.selectedItemVoteIndex} (${this.selectedItem.itemName})`
		);
		return this.selectPositionForItemAtIndex(this.selectedItemVoteIndex, positionIndex, sessionIndex);
	};

	public autoSelectNextItemWithoutPositionAfterDelay = async (): Promise<void> => {
		await new Promise((_) => setTimeout(_, 300));
		// Auto-select the next item that doesn't have a position chosen (if any)
		for (let i = this.selectedItemVoteIndex; i < this.items.length; i++) {
			if (i == this.selectedItemVoteIndex) {
				continue;
			}
			const item = this.items[i];
			if (item.positionIndex < 0) {
				this._selectedItemVoteIndex = i;
				break;
			}
		}
	};

	public positionIsSelectedForAnyItemOtherThan = (itemName: string | null, positionIndex: number): boolean => {
		if (positionIndex == -1) {
			// -1 = Nowhere, any number of items can select this
			return false;
		}
		for (let item of this.items) {
			if (item.itemName === itemName) {
				continue;
			}
			if (item.positionIndex === positionIndex) {
				return true;
			}
		}
		return false;
	};

	public positionIsSelectedForAnyItem = (positionIndex: number): boolean => {
		return this.positionIsSelectedForAnyItemOtherThan(null, positionIndex);
	};

	public itemsToPartialJSON = (): string => {
		let data: string = '';
		for (let i = 0; i < this.items.length; i++) {
			const item = this.items[i];
			if (item.positionIndex < 0) {
				continue;
			}
			data = data.concat(`"placementPos${item.positionIndex}": ${JSON.stringify(item.itemName)},`);
		}
		if (data.length > 0) {
			data = data.substring(0, data.length - 1);
		}
		return data;
	};

	private cookieHelper: CookieHelper;

	private selectPositionForItemWithName = (
		itemName: string,
		positionIndex: number,
		sessionIndex: number,
		setCookie: boolean
	): [toggled: boolean, selected: boolean] => {
		for (let i = 0; i < this.items.length; i++) {
			if (this.items[i].itemName == itemName) {
				return this.selectPositionForItemAtIndex(i, positionIndex, sessionIndex, setCookie);
			}
		}
		Log.warn(`No item with name "${itemName}", so no selection was made`);
		return [false, false];
	};

	private selectPositionForItemAtIndex = (
		itemIndex: number,
		positionIndex: number,
		sessionIndex: number,
		setCookie: boolean = true
	): [toggled: boolean, selected: boolean] => {
		Log.log(`Selecting position index ${positionIndex} for item ${itemIndex}`);
		if (itemIndex < -1 || itemIndex >= this.items.length) {
			Log.warn(`Invalid itemIndex: ${itemIndex} (expected 0 - ${this.items.length - 1})`);
			return [false, false];
		}
		if (positionIndex < -1 || positionIndex >= this.placementPositionCount) {
			Log.warn(`Invalid positionIndex: ${positionIndex} (expected 0 - ${this.placementPositionCount - 1})`);
			return [false, false];
		}

		const item = this.items[itemIndex];
		if (this.positionIsSelectedForAnyItemOtherThan(item.itemName, positionIndex)) {
			return [false, false];
		}

		if (item.positionIndex === positionIndex) {
			item.positionIndex = -1;
			Log.log(`Deselecting any position for item ${itemIndex} (${item.itemName})`);
			if (setCookie) {
				this.cookieHelper.setItemVotesForSessionIndex(sessionIndex, this.itemsToPartialJSON());
			}
			return [true, false];
		} else {
			item.positionIndex = positionIndex;
			if (setCookie) {
				this.cookieHelper.setItemVotesForSessionIndex(sessionIndex, this.itemsToPartialJSON());
			}
			return [true, true];
		}
	};

	private tryRestoreSelectionsFromCookies = (sessionIndex: number): void => {
		const selectionsJSON = this.cookieHelper.getItemVotesForSessionIndexJSON(sessionIndex);
		if (selectionsJSON === undefined || selectionsJSON == '') {
			return;
		}
		let selectedItemsForPositions: { [key: string]: string } = JSON.parse(`{${selectionsJSON}}`);
		for (let i = 0; i < this.placementPositionCount; i++) {
			const itemForPos = selectedItemsForPositions[`placementPos${i}`];
			if (itemForPos === undefined || itemForPos == '') {
				continue;
			}
			this.selectPositionForItemWithName(itemForPos, i, sessionIndex, false);
		}
	};
}

export class ItemWithPositionIndex {
	public readonly itemName: string;
	public positionIndex: number;

	public constructor(itemName: string) {
		this.itemName = itemName;
		this.positionIndex = -1; // -1 = Nowhere
	}
}
